import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import i18n from 'i18n-js';

import { settingsLine } from '@helpers';
import { isRestrictedForOperator, RESTRICTION_CATEGORIES } from '@config/operatorRestrictions';
import { RESTRICTED_SIDEBAR_TABS } from '@config/tenantRestrictions';
import { buildSettingsNavParams, isLimitedSettingsStack, type SettingsRootStack } from '@components/settings/v2/common/navigationParams';
import { getLanguageTileByValue } from '@components/settings/components/modules/changeLanguage';
import { getThemeTileByValue } from '@components/settings/components/modules/changeTheme';
import { FeatureFlagsAvailable } from '@components/featureFlags';
import type { RootState } from '@store/types';

interface UseManageAccountParams {
  lg: string;
  custom: string;
  toggleChangeLanguage: () => void;
  toggleChangeTheme: () => void;
  toggleInviteUser?: () => void;
  rootStack?: SettingsRootStack;
}

export const useManageAccount = ({
  lg,
  custom,
  toggleChangeLanguage,
  toggleChangeTheme,
  toggleInviteUser,
  rootStack = 'Settings',
}: UseManageAccountParams) => {
  const navigation = useNavigation();
  const isLimited = isLimitedSettingsStack(rootStack);

  const userInfo = useSelector((state: RootState) => state.user?.userInfo ?? {}) as Record<string, any>;
  const featureFlags = useSelector((state: RootState) => state.user?.featureFlags ?? {}) as Record<string, boolean>;

  const { userProfile = {}, companyProfile = {}, emailAddress = ' ' } = userInfo;
  const { firstName = ' ', lastName = ' ' } = userProfile;

  const filteredTabs = useMemo(
    () => {
      const base = settingsLine.filter((t: any) => {
        if (t.value === 'Account') return false;
        if (t.roles && !t.roles.includes(userInfo.role)) return false;
        if (isRestrictedForOperator(userInfo.role, RESTRICTION_CATEGORIES.SETTINGS_ITEMS, t.value)) return false;
        return true;
      });
      if (isLimited) {
        return base.filter((t: any) => (RESTRICTED_SIDEBAR_TABS as readonly string[]).includes(t.value));
      }
      return base;
    },
    [userInfo.role, isLimited],
  );

  const roundTopIndex = useMemo(() => {
    let index = 0;
    filteredTabs.some((item: any, i: number) => {
      if (FeatureFlagsAvailable(item.flag, featureFlags)) {
        index = i;
        return true;
      }
    });
    return index;
  }, [filteredTabs, featureFlags]);

  const setup = useMemo(
    () => [
      {
        name: i18n.t('Language'),
        value: getLanguageTileByValue(lg),
        onClick: toggleChangeLanguage,
      },
      {
        name: i18n.t('Dark / Light theme'),
        value: getThemeTileByValue(custom || 'system'),
        onClick: toggleChangeTheme,
      },
    ],
    [lg, custom, toggleChangeLanguage, toggleChangeTheme],
  );

  const actions = isLimited || !toggleInviteUser
    ? []
    : [
        {
          name: i18n.t('Invite Colleague'),
          onClick: toggleInviteUser,
          showHoverEffect: true,
        },
      ];

  const navigateToAccountDetails = useCallback(() => {
    navigation.navigate(rootStack as never, buildSettingsNavParams(rootStack, 'Account', 'AccountDetails') as never);
  }, [navigation, rootStack]);

  const navigateToSettingsSection = useCallback(
    (sectionName: string) => {
      navigation.navigate(rootStack as never, buildSettingsNavParams(rootStack, sectionName) as never);
    },
    [navigation, rootStack],
  );

  return {
    firstName,
    lastName,
    emailAddress,
    companyName: companyProfile.companyName,
    featureFlags,
    filteredTabs,
    roundTopIndex,
    setup,
    actions,
    navigateToAccountDetails,
    navigateToSettingsSection,
  };
};
